import { Radio } from 'lucide-react';
import { useMemo, useRef, useState, type CSSProperties, type PointerEvent, type WheelEvent } from 'react';
import type { NavigationMode } from '../App';
import type { PositionedGraph, PositionedNode } from '../lib/graph-layout';

interface OrientationMapProps {
  graph: PositionedGraph;
  selectedNodeId?: string;
  hoveredNodeId?: string;
  liveNodeIds: Set<string>;
  primaryLiveNodeId?: string;
  emergingNodeIds: Set<string>;
  navigationMode: NavigationMode;
  onSelectNode: (nodeId: string) => void;
}

interface MapPoint {
  node: PositionedNode;
  x: number;
  y: number;
}

interface MapView {
  zoom: number;
  centerX: number;
  centerY: number;
}

interface MapDragState {
  pointerId: number;
  startX: number;
  startY: number;
  startView: MapView;
  rectWidth: number;
  rectHeight: number;
}

const MAP_WIDTH = 240;
const MAP_HEIGHT = 160;
const MAP_PADDING = 14;

export function OrientationMap({
  graph,
  selectedNodeId,
  hoveredNodeId,
  liveNodeIds,
  primaryLiveNodeId,
  emergingNodeIds,
  navigationMode,
  onSelectNode
}: OrientationMapProps) {
  const map = useMemo(() => buildMap(graph), [graph]);
  const [view, setView] = useState<MapView>({ zoom: 1, centerX: MAP_WIDTH / 2, centerY: MAP_HEIGHT / 2 });
  const [isPanning, setIsPanning] = useState(false);
  const dragStart = useRef<MapDragState | undefined>(undefined);

  function project(point: Pick<MapPoint, 'x' | 'y'>) {
    return {
      x: MAP_WIDTH / 2 + (point.x - view.centerX) * view.zoom,
      y: MAP_HEIGHT / 2 + (point.y - view.centerY) * view.zoom
    };
  }

  function handleWheel(event: WheelEvent<HTMLDivElement>) {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const cursorX = ((event.clientX - rect.left) / rect.width) * MAP_WIDTH;
    const cursorY = ((event.clientY - rect.top) / rect.height) * MAP_HEIGHT;
    const nextZoom = clamp(view.zoom * (event.deltaY < 0 ? 1.18 : 0.84), 1, 5.2);
    const worldX = view.centerX + (cursorX - MAP_WIDTH / 2) / view.zoom;
    const worldY = view.centerY + (cursorY - MAP_HEIGHT / 2) / view.zoom;

    setView({
      zoom: nextZoom,
      centerX: clamp(worldX - (cursorX - MAP_WIDTH / 2) / nextZoom, 0, MAP_WIDTH),
      centerY: clamp(worldY - (cursorY - MAP_HEIGHT / 2) / nextZoom, 0, MAP_HEIGHT)
    });
  }

  function handlePanStart(event: PointerEvent<HTMLDivElement>) {
    const target = event.target;
    if (event.button !== 0 || (target instanceof HTMLElement && target.closest('button'))) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    dragStart.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startView: view,
      rectWidth: Math.max(1, rect.width),
      rectHeight: Math.max(1, rect.height)
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsPanning(true);
  }

  function handlePanMove(event: PointerEvent<HTMLDivElement>) {
    if (!dragStart.current) return;
    event.preventDefault();
    const deltaX = ((event.clientX - dragStart.current.startX) / dragStart.current.rectWidth) * MAP_WIDTH;
    const deltaY = ((event.clientY - dragStart.current.startY) / dragStart.current.rectHeight) * MAP_HEIGHT;
    setView({
      zoom: dragStart.current.startView.zoom,
      centerX: clamp(dragStart.current.startView.centerX - deltaX / dragStart.current.startView.zoom, 0, MAP_WIDTH),
      centerY: clamp(dragStart.current.startView.centerY - deltaY / dragStart.current.startView.zoom, 0, MAP_HEIGHT)
    });
  }

  function handlePanEnd(event: PointerEvent<HTMLDivElement>) {
    if (!dragStart.current) return;
    const pointerId = dragStart.current.pointerId;
    dragStart.current = undefined;
    try {
      event.currentTarget.releasePointerCapture(pointerId);
    } catch {
      // Pointer capture may already be released if the browser cancels the gesture.
    }
    setIsPanning(false);
  }

  return (
    <div className="orientation-map" aria-label="Orientierung im Vault-Kosmos">
      <div
        className={isPanning ? 'orientation-map__plot is-panning' : 'orientation-map__plot'}
        role="img"
        aria-label="Top-down-Karte des aktuellen Graphlayouts"
        onWheel={handleWheel}
        onPointerDown={handlePanStart}
        onPointerMove={handlePanMove}
        onPointerUp={handlePanEnd}
        onPointerCancel={handlePanEnd}
        onDoubleClick={() => setView({ zoom: 1, centerX: MAP_WIDTH / 2, centerY: MAP_HEIGHT / 2 })}
        title="Mausrad zoomt, mit linker Maustaste ziehen, Doppelklick setzt zurück"
      >
        <svg viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`} aria-hidden="true">
          <rect x="1" y="1" width={MAP_WIDTH - 2} height={MAP_HEIGHT - 2} rx="8" />
          <g transform={`translate(${MAP_WIDTH / 2} ${MAP_HEIGHT / 2}) scale(${view.zoom}) translate(${-view.centerX} ${-view.centerY})`}>
            {map.areaRings.map((ring) => (
              <circle
                key={ring.area}
                className="orientation-map__area"
                cx={ring.x}
                cy={ring.y}
                r={ring.radius}
                style={{ '--area-color': ring.color } as CSSProperties}
              />
            ))}
          </g>
        </svg>
        {map.points.map((point) => {
          const projected = project(point);
          const isSelected = selectedNodeId === point.node.id;
          const isHovered = hoveredNodeId === point.node.id;
          const isLive = liveNodeIds.has(point.node.id);
          const isPrimaryLive = primaryLiveNodeId === point.node.id;
          const isEmerging = emergingNodeIds.has(point.node.id);
          const className = [
            'orientation-map__node',
            `orientation-map__node--${point.node.kind}`,
            isSelected ? 'is-selected' : '',
            isHovered ? 'is-hovered' : '',
            isLive ? 'is-live' : '',
            isPrimaryLive ? 'is-primary-live' : '',
            isEmerging ? 'is-emerging' : ''
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={point.node.id}
              className="orientation-map__hit"
              type="button"
              style={{ left: `${(projected.x / MAP_WIDTH) * 100}%`, top: `${(projected.y / MAP_HEIGHT) * 100}%` }}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => onSelectNode(point.node.id)}
              title={point.node.path || point.node.label}
            >
              <svg viewBox="0 0 12 12" aria-hidden="true">
                <circle
                  className={className}
                  cx="6"
                  cy="6"
                  r={nodeMarkerRadius(point.node)}
                  style={{ '--node-color': point.node.color } as CSSProperties}
                />
              </svg>
            </button>
          );
        })}
      </div>

      <div className="orientation-map__meta">
        <span>
          <Radio size={12} />
          {navigationMode === 'fly' ? 'Flug' : navigationMode === 'deep' ? 'Tiefe' : navigationMode === 'focus' ? 'Fokus' : 'Übersicht'}
        </span>
        <strong>{view.zoom.toFixed(1)}x · {graph.nodes.length}</strong>
      </div>
    </div>
  );
}

function buildMap(graph: PositionedGraph) {
  const nodes = graph.nodes.filter((node) => node.area !== 'Vault');
  const xs = nodes.map((node) => node.position[0]);
  const zs = nodes.map((node) => node.position[2]);
  const minX = Math.min(...xs, -1);
  const maxX = Math.max(...xs, 1);
  const minZ = Math.min(...zs, -1);
  const maxZ = Math.max(...zs, 1);
  const spanX = Math.max(1, maxX - minX);
  const spanZ = Math.max(1, maxZ - minZ);

  const points: MapPoint[] = nodes.map((node) => ({
    node,
    x: MAP_PADDING + ((node.position[0] - minX) / spanX) * (MAP_WIDTH - MAP_PADDING * 2),
    y: MAP_PADDING + ((node.position[2] - minZ) / spanZ) * (MAP_HEIGHT - MAP_PADDING * 2)
  }));

  const buckets = new Map<string, { color: string; points: MapPoint[] }>();
  for (const point of points) {
    if (point.node.kind === 'unresolved') continue;
    const bucket = buckets.get(point.node.area) ?? { color: point.node.color, points: [] };
    bucket.points.push(point);
    buckets.set(point.node.area, bucket);
  }

  const areaRings = [...buckets.entries()]
    .map(([area, bucket]) => {
      const x = average(bucket.points.map((point) => point.x));
      const y = average(bucket.points.map((point) => point.y));
      const radius = Math.max(
        14,
        Math.min(42, Math.sqrt(bucket.points.length) * 3.2 + maxDistance(bucket.points, x, y) * 0.42)
      );
      return { area, color: bucket.color, x, y, radius };
    })
    .sort((a, b) => b.radius - a.radius)
    .slice(0, 9);

  return { points, areaRings };
}

function nodeMarkerRadius(node: PositionedNode) {
  if (node.kind === 'folder') return 3.6;
  if (node.kind === 'unresolved') return 2.3;
  return Math.max(2.2, Math.min(3.4, 2.2 + node.radius * 1.2));
}

function average(values: number[]) {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function maxDistance(points: MapPoint[], x: number, y: number) {
  return points.reduce((max, point) => Math.max(max, Math.hypot(point.x - x, point.y - y)), 0);
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}
